import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useResetRecoilState } from "recoil";
import { Box, Collapse } from "@mui/material";
import { OcConfirmationDialog, OcRoleFilter } from "oc/components";
import { ProductsT, RoleCategoriesT } from "oc/configs/constants";
import { RoleFilterInput } from "oc/types";
import { ocRoleFilteringState } from "states";

type OcRoleFilteringProps = {
  showRoleFiltering: boolean;
  setShowRoleFiltering: (show: boolean) => void;
  onAddRole: () => void;
  product: string;
  role: string;
  roleFiltering: RoleFilterInput;
  onAddRoleFiltering: (filter: any) => void;
  onRemoveRoleFiltering: (filter: any) => void;
  isMobile: boolean;
};

export const OcRoleFiltering = ({
  showRoleFiltering,
  setShowRoleFiltering,
  onAddRole,
  product,
  role,
  roleFiltering,
  onAddRoleFiltering,
  onRemoveRoleFiltering,
  isMobile,
}: OcRoleFilteringProps) => {
  const { t } = useTranslation();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  let isMyHRAdmin = product === "MyHR" && role === "admin";
  let isUser = role === "user";

  const resetRoleFiltering = useResetRecoilState(ocRoleFilteringState);

  const handleCloseCollapse = () => {
    setShowRoleFiltering(false);
  };

  const handleCancel = () => {
    resetRoleFiltering();
    handleCloseCollapse();
  };

  const handleAddRole = () => {
    const isEmptyFilters = Object.values(roleFiltering).every(
      (filter) => Array.isArray(filter) && filter.length === 0,
    );

    if (
      ["examiner", "viewer", "uploader", "assigner"].includes(role) &&
      isEmptyFilters
    ) {
      setShowConfirmDialog(true);
      return;
    }
    onAddRole();
  };

  const handleDialogConfirm = () => {
    setShowConfirmDialog(false);
    onAddRole();
  };

  const handleDialogCancel = () => {
    setShowConfirmDialog(false);
  };

  return (
    <Collapse in={showRoleFiltering} timeout="auto" unmountOnExit>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        paddingTop="24px"
      >
        <Box padding="24px" width={isMobile ? "100%" : "696px"}>
          <OcRoleFilter
            subtypeProps={{
              queryType: "admin",
              roleFilters: roleFiltering,
              handleAddItem: onAddRoleFiltering,
              handleRemoveItem: onRemoveRoleFiltering,
              role: role as RoleCategoriesT,
              product: product as ProductsT,
            }}
            noFilters={isUser || isMyHRAdmin}
            noCollapse={isUser || isMyHRAdmin}
            defaultOpened
            innerWidth="100%"
            buttonBarProps={{
              noLine: true,
              paddingTop: "10px",
              handlePrimaryButton: handleAddRole,
              primaryButtonText: t("OC_SAVE"),
              handleRightButton: handleCancel,
              rightButtonColor: "primary",
              rightButtonText: t("OC_DELETE"),
            }}
            chipData={{
              product,
              role,
            }}
          />
        </Box>
      </Box>
      <OcConfirmationDialog
        showDialog={showConfirmDialog}
        onClose={handleDialogConfirm}
        onCancel={handleDialogCancel}
        title={t("OC_CONFIRM_SETTING")}
        texts={[t("MY_HR_ADD_ALL_PERMISSIONS_DIALOG_INFO")]}
        okButtonText={t("OC_ALL_RIGHT")}
        cancelButtonText={t("OC_CANCEL")}
      />
    </Collapse>
  );
};

export default OcRoleFiltering;
